import React, { useEffect, useRef, useState } from 'react';
import styles from './RevenueVendorExpenseTable.module.scss';
import Headers from './components/Header';
import Rows from './components/Rows';
import InfiniteScroll from 'react-infinite-scroll-component';
import LoadingSpinner from '../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import { fetchRevenueVendorExpenseTableData } from '../../../../../store/dashboard/revenueVendorExpenses/revenueVendorExpensesApiHelpers';
import Tooltip from '../../../../../common/_custom/Tooltip/Tooltip';
import { infoIconGreen, overlapInfoIconGrey } from '../../../../../assets/hostedassets';
import clsx from 'clsx';
import { infoTooltipB2b, infoTooltTipVendorExpenses } from '../GSTR3BTable/gstr3Constants';
import HeaderBodyBox from '../../../../../common/HeaderBodyBox/HeaderBodyBox';
import { SORT_DIRECTIONS, SortConfig, TabModule } from '../../../../../utils/constants/commonConstants';
import useRevenueTableRows, { RevenueTableData } from './useRevenueTableRows';

// fetchStartDate/fetchEndDate: applied GST "Time Period" range (RECUR-27384). UW's
// gstInvoicesPivotData has no rangeKey concept — only explicit dates — so ALL
// (fetchStartDate === '') omits them entirely (unchanged default trailing window).
function RevenueVendorExpenseTable({ tab = '', dataSource, fetchStartDate = '', fetchEndDate = '' }) {
    const [fetchingData, setFetchingData] = useState(false);
    const [tableData, setTableData] = useState<RevenueTableData>({
        fetchSuccess: false,
        headers: [],
        rows: [],
        totalRecords: 0,
        records: 0,
    });
    const [pageNum, setPageNum] = useState(1);
    const [sortConfig, setSortConfig] = useState<SortConfig | undefined>(undefined);
    const tableRef = useRef(null);
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    // RECUR-27384 review fix: bumped on every fetch trigger (mount, range change) so an
    // out-of-order response — or an in-flight page append — from a superseded range is
    // dropped instead of overwriting/appending onto newer data. Page loads (InfiniteScroll's
    // `next`) belong to whichever generation is current at the moment they're triggered
    // (captured via the default param on fetchTableData below).
    const fetchGenRef = useRef(0);

    // Column sort (Total + month columns) — cycle none → ASC → DESC → none on the shared
    // SortConfig shape; switching to a different column starts it at ASC.
    const onSortColumn = (field: string) =>
        setSortConfig((c) => {
            if (c?.field !== field) return { field, direction: SORT_DIRECTIONS.ASC };
            const next =
                c.direction === SORT_DIRECTIONS.ASC
                    ? SORT_DIRECTIONS.DESC
                    : c.direction === SORT_DIRECTIONS.DESC
                    ? null
                    : SORT_DIRECTIONS.ASC;
            return next ? { field, direction: next } : undefined;
        });

    const noDataAvailable = tableData.fetchSuccess && !tableData?.rows?.length;
    const noGSTConnected = parseInt(dataSource?.[1]?.text?.[0]) === 0;

    const isRevenue = tab === 'revenue';
    // Derive the B2B display headers + rows (Circular/Net columns, Intra + Net B2B Revenue rows,
    // Total-column sort). Pure logic lives in useRevenueTableRows so the money math is unit-tested.
    const { displayHeaders, displayRows } = useRevenueTableRows(tableData, isRevenue, sortConfig);

    const dataSourceList = dataSource?.[1]?.gstList || [];

    useEffect(() => {
        fetchGenRef.current += 1;
        fetchTableData(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // On first load, land on the LATEST month: months run oldest→newest left-to-right with the
    // pinned Total/Circular/Net at the right, so start the horizontal scroll at the right end.
    const initScrolled = useRef(false);
    useEffect(() => {
        if (initScrolled.current || !tableData?.rows?.length) return;
        const el = scrollContainerRef.current;
        if (el) {
            el.scrollLeft = el.scrollWidth;
            initScrolled.current = true;
        }
    }, [tableData?.rows?.length]);

    // Applying a new Time Period range starts the table over from page 1 — clear the
    // accumulated InfiniteScroll rows and refetch, skipping the initial mount (that
    // fetch is already covered by the effect above).
    const isRangeMountRef = useRef(true);
    useEffect(() => {
        if (isRangeMountRef.current) {
            isRangeMountRef.current = false;
            return;
        }
        // Bump the generation BEFORE clearing/refetching so an in-flight page append from
        // the old range (e.g. a page-2 request the user triggered before switching range)
        // resolves after this point and gets dropped instead of appending onto the reset table.
        fetchGenRef.current += 1;
        setTableData({ fetchSuccess: false, headers: [], rows: [], totalRecords: 0, records: 0 });
        setPageNum(1);
        initScrolled.current = false;
        fetchTableData(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchStartDate, fetchEndDate]);

    const fetchTableData = (page = pageNum, gen = fetchGenRef.current) => {
        if (page < 2) setFetchingData(true);
        const rangeParams = fetchStartDate ? { startDate: fetchStartDate, endDate: fetchEndDate } : {};
        fetchRevenueVendorExpenseTableData(
            {
                gstType: tab === 'revenue' ? 'REVENUE' : tab === 'vendorExpenses' ? 'EXPENSE' : '',
                excludeGstR3: tab === 'revenue',
                pageNum: page,
                pageSize: 50,
                ...rangeParams,
            },
            {
                onSuccess: (data) => onDataFetchSuccess(data, page, gen),
                onError: () => {
                    if (gen === fetchGenRef.current) setFetchingData(false);
                },
            },
        );
    };

    const onDataFetchSuccess = (data, page, gen: number) => {
        if (gen !== fetchGenRef.current) return; // stale response from a superseded range — drop it
        if (page > 1) {
            setTableData((tableData) => {
                const newRows = data?.rows || [];
                if (newRows?.length) {
                    tableData?.rows?.pop();
                    return {
                        ...tableData,
                        rows: [...tableData.rows, ...newRows],
                    };
                } else return tableData;
            });
        } else
            setTableData({
                fetchSuccess: true,
                ...data,
            });
        setFetchingData(false);
        setPageNum(page + 1);
    };

    const getTitle = () => {
        switch (tab) {
            case 'revenue':
                return 'B2B Customer Summary';
            case 'vendorExpenses':
                return 'Vendor Expense From GST';
        }
    };

    // const getSecondaryText = () => {
    //     switch (tab) {
    //         case 'revenue':
    //             return '(Data from GSTR-1)';
    //         case 'vendorExpenses':
    //             return '(Data from GSTR-2)';
    //     }
    // };

    const getDataSource = () => {
        if (tab !== 'revenue') return <></>;
        const connectedGsts = dataSourceList.filter((gst) => gst?.connected);
        return (
            <div className={styles.DataSource}>
                <div className={styles.Content}>
                    Data Source :&nbsp;
                    <div
                        className={clsx(styles.DataSourceBadge, {
                            [styles.Green]: connectedGsts?.length >= dataSourceList?.length,
                        })}
                    >
                        {connectedGsts?.length} / {dataSourceList?.length} GST Connected
                        <Tooltip content={getGSTList()} direction={'left'}>
                            <img src={infoIconGreen} alt="" height="14px" />
                        </Tooltip>
                    </div>
                </div>
            </div>
        );
    };

    const getGSTList = () => {
        return (
            <div className={styles.GstList}>
                {dataSourceList.map((gst) => (
                    <div key={gst?.gstNumber} className={styles.GstItem}>
                        <div>
                            <span>{gst?.gstNumber}</span>
                            {/*<span>{gst?.gstStatus}</span>*/}
                        </div>
                        <div
                            className={clsx(styles.Status, {
                                [styles.Connected]: gst?.connected,
                            })}
                        >
                            {gst?.connected ? 'Connected' : 'Not Connected'}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderHeaderRight = () => {
        if (fetchingData) {
            return <LoadingSpinner color={'#6021b3'} height={'20px'} />;
        }
        if (noDataAvailable) {
            return 'No Data Available';
        }
        return getDataSource();
    };

    const renderTableBody = () => {
        if (noDataAvailable || fetchingData || !tableData?.rows?.length) {
            return null;
        }

        return (
            <div className={styles.TableContainer} id="revenue-table-scroll-container" ref={scrollContainerRef}>
                <InfiniteScroll
                    dataLength={tableData?.rows?.length}
                    hasMore={tableData.rows?.length + 1 < tableData.totalRecords}
                    next={fetchTableData}
                    loader={false}
                    scrollableTarget="revenue-table-scroll-container"
                    style={{ overflow: 'visible' }}
                >
                    <table className={clsx(styles.BankTable, { [styles.VendorExpenseTable]: tab === TabModule.VENDOR_EXPENSES })}>
                        <thead className={styles.TableHead}>
                            <Headers
                                data={displayHeaders}
                                sortConfig={isRevenue ? sortConfig : undefined}
                                onSort={isRevenue ? onSortColumn : undefined}
                            />
                        </thead>
                        <tbody className={styles.TableBody}>
                            <Rows rows={displayRows} headers={displayHeaders} tableRef={tableRef} />
                        </tbody>
                    </table>
                </InfiniteScroll>
            </div>
        );
    };

    return (
        <div className={styles.RevenueVendorExpenseTable}>
            <HeaderBodyBox
                title={
                    <div className={styles.Title}>
                        <span className={styles.Main}>{getTitle()}</span>
                        <Tooltip content={tab === 'revenue' ? infoTooltipB2b : infoTooltTipVendorExpenses} direction={'right'}>
                            <img src={overlapInfoIconGrey} alt="info" height={'18px'} />
                        </Tooltip>
                    </div>
                }
                headerRight={renderHeaderRight()}
                className={clsx({
                    [styles.TopNoData]: fetchingData || noDataAvailable,
                })}
            >
                {renderTableBody()}
            </HeaderBodyBox>
        </div>
    );
}

export default RevenueVendorExpenseTable;
